import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import MetricBuilders, {
	type MetricBuildersEntryPointProps,
} from '@databox/ember-integrations/lib/components/MetricBuilders/MetricBuildersEntryPoint';

import type { CreateRecordProperties, ReactIntegrations, RecordData, Service } from 'webapp-types';
import { MetricType, ModelName, ProductIssueType } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { getShadowDomMode, isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

import type MetricRecord from 'webapp/models/metric';

interface MetricDefinitionSettings {
	format?: string | null;
	changeFormat?: string | null;
	changeInvert?: boolean;
	changeFunction?: number | null;
	noReset?: boolean;
}

interface MetricMetadata {
	description?: string | null;
}

interface Args {
	sourceId: number;
	onSave?: () => void;
	onCancel?: () => void;
	editMetricId?: number;
	editMetricName?: string;
	isClone?: boolean;
	viewOnly?: boolean;
}

/**
 * Mounts the Horizon metric builder (exposed by `@databox/ember-integrations`) inline in the
 * custom-query setup form, using the same React mount mechanism as
 * `CustomQuery::Setup::Datasets::Datasets` (reactService.create in `didInsert`).
 */
export default class CustomQuerySetupHorizonComponent extends Component<Args> {
	reactElement: ReactIntegrations.Root = null;
	private previewCloseCallback: (() => void) | null = null;

	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('react') declare reactService: Service.React;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('store') declare storeService: Service.Store;
	@service('issues-report') declare issuesReportService: Service.IssuesReport;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;

	@action
	setupView(node: HTMLElement): void {
		this.reactElement = this.reactService.create(node as HTMLDivElement);
		// Selected source is carried on the node; ready to thread into the builder once the
		// exposed MetricBuilders export accepts a source. Kept on the node for debugging/tests.
		node.dataset.horizonSourceId = String(this.args.sourceId);
		this.renderMetricBuilder();

		this.eventManagerService.on(
			AppEvent.ACCESS_TOKEN_REFRESHED,
			this,
			this.renderMetricBuilder,
		);
		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderMetricBuilder);
	}

	@action
	teardownView(): void {
		this.previewCloseCallback?.();
		this.previewCloseCallback = null;

		this.reactService.unmount(this.reactElement);

		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
	}

	@action
	handleSaveSuccess(response: RecordData.HorizonMetric.Raw): void {
		this.storeService.pushPayload(ModelName.HORIZON_METRIC, {
			'horizon-metrics': [response],
		});
		this.infolayerService.ok(`Metric "${response.name}" saved!`);
		const onSave = this.args.onSave;
		void this.requestService.storeFindAll(ModelName.METRIC).then(() => {
			onSave?.();
		});
	}

	@action
	handleEditMetricDefinition(
		currentSettings: MetricDefinitionSettings | null,
		currentMetadata: MetricMetadata | null,
		onResult: (settings: MetricDefinitionSettings, metadata?: MetricMetadata) => void,
	): void {
		const source = this.mmSourcesService.findModelQuick(String(this.args.sourceId));
		if (!source) return;

		const baseSettings: Record<string, unknown> = {
			objectType: MetricType.HORIZON,
			spaceAccessId: this.args.sourceId,
			dataSourceTypeKey: source.sourceType.key,
		};

		if (currentSettings) {
			Object.assign(baseSettings, {
				format: currentSettings.format ?? null,
				changeFormat: currentSettings.changeFormat ?? null,
				changeInvert: !!currentSettings.changeInvert,
				changeFunction: currentSettings.changeFunction ?? null,
				noReset: !!currentSettings.noReset,
			});
		}

		if (currentMetadata?.description) {
			baseSettings.description = currentMetadata.description;
		}

		const normalizedSettings = this.storeService.normalize(ModelName.METRIC, baseSettings).data
			.attributes as CreateRecordProperties<MetricRecord>;

		const model = this.mmMetricsService.createNewModel(normalizedSettings);

		this.mmMetricsService.openMetricDefinitionPopup(
			model,
			true,
			true,
			true,
			undefined,
			(metricSettingsData: RecordData.Metric.Serialized) => {
				onResult(
					{
						format: metricSettingsData.format || null,
						changeFormat: metricSettingsData.changeFormat || null,
						changeInvert: !!metricSettingsData.changeInvert,
						changeFunction: metricSettingsData.changeFunction ?? null,
						noReset: !!metricSettingsData.noReset,
					},
					{ description: metricSettingsData.description || null },
				);
			},
		);
	}

	@action
	handleError(info: {
		message: string;
		httpStatusCode: number;
		requestId: string;
		endpoint: string;
	}): void {
		if (!isAlive(this)) return;
		const eventIssueData = {
			type: ProductIssueType.GENERAL_ERROR,
			page: 'metric_builder',
			url: info.endpoint,
			errorMessage: info.message,
			feature: 'horizon_metric_builder',
			endpoint: info.endpoint,
			httpStatusCode: info.httpStatusCode,
			requestId: info.requestId,
		};

		this.issuesReportService.triggerEventIssue(eventIssueData);
		this.issuesReportService.showInfolayerError(info.message, {
			endpoint: info.endpoint,
			httpStatusCode: info.httpStatusCode,
			errorText: info.message,
			requestId: info.requestId,
			eventIssueData,
		});
	}

	@action
	handlePreviewTakingTooLong(): () => void {
		const infolayer = this.infolayerService.loading(
			'The preview is taking longer than usual. You can save the metric anyways.',
		);
		infolayer.expire(30000);

		const closeCallback = () => infolayer.close();
		this.previewCloseCallback = closeCallback;

		return closeCallback;
	}

	@action
	renderMetricBuilder(): void {
		if (!this.reactElement) {
			return;
		}

		const props: MetricBuildersEntryPointProps = {
			accessToken: this.currentUserService.accessToken!,
			dataToken: this.currentUserService.userDataToken!,
			spaceId: this.accountService.id,
			spaceAccessId: this.args.sourceId,
			isDarkThemeActive: this.navigationService.theme.isDark,
			onSave: this.args.viewOnly ? undefined : this.handleSaveSuccess,
			onCancel: this.args.onCancel,
			onEditMetricDefinition: this.args.viewOnly
				? undefined
				: this.handleEditMetricDefinition,
			onError: this.handleError,
			onPreviewTakingTooLong: this.handlePreviewTakingTooLong,
			editMetricId: this.args.editMetricId,
			editMetricName: this.args.editMetricName,
			isClone: this.args.isClone,
		};

		this.reactService.render(
			this.reactElement,
			MetricBuilders(props),
			getShadowDomMode(this.accountService.email),
		);
	}
	<template>
		<div
			id="horizon-metric-builder"
			{{didInsert this.setupView}}
			{{didUpdate this.renderMetricBuilder @sourceId}}
			{{willDestroy this.teardownView}}
		>
		</div>
	</template>
}
